import React, { useState, useEffect } from "react";
import AppointmentCalendar from "../components/appointments/AppointmentCalendar";
import AppointmentSidebar from "../components/appointments/AppointmentSidebar";
import AppointmentModal from "../components/appointments/AppointmentModal";
import AppointmentService, {
  Appointment,
  AppointmentFormData,
  NewPatientData,
} from "../services/appointmentService";
import PatientService from "../services/patientService";
import { useAuth } from "../hooks/useAuth";
import getIcon from "../utils/Icons";

// Add after the imports
interface SelectedService {
  service: {
    id: number;
    name: string;
    base_fee: number;
    clinic_percentage: number;
    provider_percentage: number;
    owner_percentage: number;
    referrer_percentage: number;
  };
  referrer_name: string;
  customPrice?: number;
  pricingReason?: string;
}
const Appointments: React.FC = () => {
  const { user } = useAuth(); // Add auth context
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<"day" | "week" | "month">("week");
  const [selectedDoctors, setSelectedDoctors] = useState<number[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [todaysAppointments, setTodaysAppointments] = useState<Appointment[]>(
    []
  );
  const [doctors, setDoctors] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">(
    "create"
  );
  const [selectedSlot, setSelectedSlot] = useState<{
    date: string;
    time: string;
    doctorId?: number;
  } | null>(null);
  const [prefilledPatient, setPrefilledPatient] = useState<{
    id: number;
    name: string;
    phone: string;
  } | null>(null);

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  const formatDate = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };
  const generateDateColumns = (): Date[] => {
    if (view === "day") {
      return [new Date(currentDate)];
    } else if (view === "week") {
      // Week view
      const dates: Date[] = [];
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        dates.push(date);
      }
      return dates;
    } else {
      // Month view
      const dates: Date[] = [];
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const startDate = new Date(firstDay);
      startDate.setDate(firstDay.getDate() - firstDay.getDay());

      const endDate = new Date(lastDay);
      endDate.setDate(lastDay.getDate() + (6 - lastDay.getDay()));

      for (
        let d = new Date(startDate);
        d <= endDate;
        d.setDate(d.getDate() + 1)
      ) {
        dates.push(new Date(d));
      }

      return dates;
    }
  };
  const dateColumns = generateDateColumns();
  const filteredDoctors = doctors.filter((doctor) =>
    selectedDoctors.includes(doctor.id)
  );
  const [isWalkInModalOpen, setIsWalkInModalOpen] = useState(false);
  const [selectedWalkInPatient, setSelectedWalkInPatient] = useState<any>(null);
  // Mobile responsive states
  const [isMobile, setIsMobile] = useState(false);
  const [mobileBottomSheetOpen, setMobileBottomSheetOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const handleWalkInPatient = () => {
    setIsWalkInModalOpen(true);
  };
  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      const wasMobile = isMobile;
      setIsMobile(mobile);

      if (mobile) {
        // setView("day"); // Default to day view on mobile

        // If switching from desktop to mobile and multiple doctors are selected,
        // keep only the first selected doctor
        if (!wasMobile && selectedDoctors.length > 1) {
          setSelectedDoctors([selectedDoctors[0]]);
        }
      }
    };

    // Handle single doctor selection from sidebar on mobile
    const handleSelectSingleDoctor = (event: any) => {
      if (isMobile) {
        setSelectedDoctors([event.detail.doctorId]);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    window.addEventListener("selectSingleDoctor", handleSelectSingleDoctor);

    return () => {
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener(
        "selectSingleDoctor",
        handleSelectSingleDoctor
      );
    };
  }, [isMobile, selectedDoctors]);

  // Load initial data
  // Update your existing useEffect (around line 175) to handle calendar navigation
  useEffect(() => {
    loadInitialData();

    // Check for URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const patientId = urlParams.get("bookForPatient");
    const doctorId = urlParams.get("doctorId");
    const highlightId = urlParams.get("highlight");
    const dateParam = urlParams.get("date");
    const doctorParam = urlParams.get("doctor");
    const viewParam = urlParams.get("view");

    // Handle patient booking from URL
    if (patientId) {
      loadPatientForBooking(
        parseInt(patientId),
        doctorId ? parseInt(doctorId) : undefined
      );
    }

    // Handle appointment highlighting from patient history
    if (highlightId && dateParam) {
      handleCalendarNavigation(
        dateParam,
        parseInt(highlightId),
        doctorParam ? parseInt(doctorParam) : undefined,
        viewParam || undefined
      );
    }
  }, []);

  // Add this new handler function (around line 190)
  const handleCalendarNavigation = async (
    date: string,
    appointmentId: number,
    doctorId?: number,
    viewParam?: string
  ) => {
    try {
      // Parse date correctly to avoid timezone issues
      const [year, month, day] = date.split("-").map(Number);
      const appointmentDate = new Date(year, month - 1, day, 12, 0, 0);

      // ✅ Set all state synchronously
      setCurrentDate(appointmentDate);

      if (viewParam === "day") {
        setView("day");
      }

      if (doctorId) {
        setSelectedDoctors([doctorId]);
      } else if (doctors.length > 0) {
        const firstDoctor = [...doctors].sort((a, b) => a.id - b.id)[0];
        setSelectedDoctors([firstDoctor.id]);
      }

      // ✅ FIX: Wait longer for React to batch all state updates
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // ✅ FIX: Manually load appointments with the correct date range
      // Instead of relying on useEffect, fetch directly with known parameters
      const toYMD = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dayStr = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${dayStr}`;
      };

      // Fetch appointments for the specific date
      const appointmentsData = await AppointmentService.getAppointments({
        date: toYMD(appointmentDate),
      });

      // Filter for selected doctor if specified
      let filteredAppointments = appointmentsData.data || [];
      if (doctorId) {
        filteredAppointments = filteredAppointments.filter(
          (apt) => apt.doctor_id === doctorId
        );
      }

      // Update state with fetched appointments
      setAppointments(filteredAppointments);
      setTodaysAppointments(filteredAppointments);

      // Wait for state to update
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Find the specific appointment
      const appointment = filteredAppointments.find(
        (apt) => apt.id === appointmentId
      );

      if (appointment) {
        // Scroll to the appointment's time slot
        scrollToAppointmentTime(date, appointment.appointment_time);

        // Open the appointment modal after scrolling
        setTimeout(() => {
          handleAppointmentClick(appointment);
        }, 700);
      } else {
        console.warn(
          "⚠️ Appointment not found. All appointments:",
          filteredAppointments.map((a) => ({
            id: a.id,
            date: a.appointment_date,
          }))
        );
        // Scroll to a default time
        scrollToAppointmentTime(date, "09:00");
      }

      // Clean up URL parameters
      const url = new URL(window.location.href);
      url.searchParams.delete("highlight");
      url.searchParams.delete("date");
      url.searchParams.delete("doctor");
      url.searchParams.delete("view");
      window.history.replaceState({}, "", url.toString());
    } catch (error) {
      console.error("Failed to navigate to appointment:", error);
    }
  };

  // Add this helper function to scroll to the appointment time
  const scrollToAppointmentTime = (date: string, time: string) => {
    try {
      // Find the calendar container
      const calendarContainer = document.querySelector(".calendar-container");
      if (!calendarContainer) return;

      // Parse the appointment time
      const [hours, minutes] = time.split(":").map(Number);

      // Calculate the time slot element
      // Each hour has 4 slots (15-min intervals), starting from 8 AM
      const slotsFromStart = (hours - 8) * 4 + Math.floor(minutes / 15);
      const slotHeight = isMobile ? 50 : 60;
      const headerHeight = isMobile ? 50 : 60;

      // Calculate scroll position (offset to center the appointment)
      const scrollPosition =
        slotsFromStart * slotHeight - window.innerHeight / 3 + headerHeight;

      // Smooth scroll to the appointment
      calendarContainer.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior: "smooth",
      });

      // Highlight the time slot briefly
      setTimeout(() => {
        const timeSlots = document.querySelectorAll("[data-time-slot]");
        timeSlots.forEach((slot: any) => {
          if (slot.dataset.timeSlot === time) {
            slot.style.transition = "background-color 0.3s ease";
            slot.style.backgroundColor = "#dbeafe";

            setTimeout(() => {
              slot.style.backgroundColor = "";
            }, 2000);
          }
        });
      }, 400);
    } catch (error) {
      console.error("Failed to scroll to appointment:", error);
    }
  };

  // Load appointments when date or view changes
  useEffect(() => {
    loadAppointments();
  }, [currentDate, view, selectedDoctors]);
  useEffect(() => {
    const calendarContainer = document.querySelector(".calendar-container");
    if (calendarContainer) {
      // Restore scroll position from localStorage
      const savedScrollPos = localStorage.getItem("calendar-scroll-x");
      if (savedScrollPos) {
        calendarContainer.scrollLeft = parseInt(savedScrollPos);
      }

      // Save scroll position on scroll
      const handleScroll = () => {
        localStorage.setItem(
          "calendar-scroll-x",
          calendarContainer.scrollLeft.toString()
        );
      };

      calendarContainer.addEventListener("scroll", handleScroll);
      return () =>
        calendarContainer.removeEventListener("scroll", handleScroll);
    }
  }, [view, currentDate]);
  const loadPatientForBooking = async (
    patientId: number,
    doctorId?: number
  ) => {
    try {
      const patient = await PatientService.getPatient(patientId);
      setPrefilledPatient({
        id: patient.id,
        name: `${patient.first_name} ${patient.last_name}`,
        phone: patient.phone,
      });

      // Create selectedSlot with doctor ID if provided
      if (doctorId) {
        setSelectedSlot({
          date: new Date().toISOString().split("T")[0], // Today's date
          time: "09:00", // Default time
          doctorId: doctorId,
        });
      } else {
        setSelectedSlot(null);
      }

      // Open modal for new appointment
      setSelectedAppointment(null);
      setModalMode("create");
      setIsModalOpen(true);

      // Clean up URL - remove both parameters
      const url = new URL(window.location.href);
      url.searchParams.delete("bookForPatient");
      url.searchParams.delete("doctorId");
      window.history.replaceState({}, "", url.toString());
    } catch (error) {
      console.error("Failed to load patient for booking:", error);
    }
  };

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      const [doctorsData, patientsData] = await Promise.all([
        PatientService.getDoctors(),
        PatientService.getPatients({ per_page: 100 }),
      ]);

      setDoctors(doctorsData);
      setPatients(patientsData.data || []);

      // UPDATED: Handle doctor selection based on user role - SELECT FIRST DOCTOR BY ID
      if (doctorsData && doctorsData.length > 0) {
        let defaultSelectedDoctors: number[] = [];

        // FIXED: Sort doctors by ID to ensure consistent "first" doctor
        const sortedDoctors = [...doctorsData].sort((a, b) => a.id - b.id);
        const firstDoctorId = sortedDoctors[0].id;

        // Check user role and set appropriate doctor selection
        if (user?.roles?.includes("admin") || user?.role === "admin") {
          // CHANGED: Admin now selects only the first doctor by default (can add more)
          defaultSelectedDoctors = [firstDoctorId];
        } else if (
          user?.roles?.includes("secretary") ||
          user?.role === "secretary"
        ) {
          // Check if main secretary or regular secretary
          if (user.is_main_secretary) {
            // Main secretary: Select first doctor by default (can add more)
            defaultSelectedDoctors = [firstDoctorId];
          } else if (user.assigned_doctor_id) {
            // Regular secretary: Only select assigned doctor (cannot change)
            const assignedDoctor = doctorsData.find(
              (d: any) => d.id === user.assigned_doctor_id
            );
            if (assignedDoctor) {
              defaultSelectedDoctors = [user.assigned_doctor_id];
            } else {
              defaultSelectedDoctors = [firstDoctorId];
            }
          } else {
            defaultSelectedDoctors = [firstDoctorId];
          }
        } else if (user?.roles?.includes("doctor") || user?.role === "doctor") {
          // Doctor: Only select themselves (cannot change)
          const doctorExists = doctorsData.find((d: any) => d.id === user.id);
          if (doctorExists) {
            defaultSelectedDoctors = [user.id];
          } else {
            defaultSelectedDoctors = [firstDoctorId];
          }
        } else {
          // Default: Select first doctor
          defaultSelectedDoctors = [firstDoctorId];
        }

        setSelectedDoctors(defaultSelectedDoctors);
      } else {
        console.log(`${getIcon("warning")} No doctors found in response`);
      }
    } catch (err: any) {
      console.error(`${getIcon("error")} Error loading initial data:`, err);
      setError(err.message || "Failed to load initial data");
    } finally {
      setIsLoading(false);
    }
  };
  const handleDoctorSelection = {
    selectFirst: () => {
      const firstDoctor = [...doctors].sort((a, b) => a.id - b.id)[0];
      if (firstDoctor) {
        setSelectedDoctors([firstDoctor.id]);
      }
    },
    selectAll: () => {
      setSelectedDoctors(doctors.map((d) => d.id));
    },
    clearAll: () => {
      setSelectedDoctors([]);
    },
  };
  const loadAppointments = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange();

      const filters = {
        start_date: startDate,
        end_date: endDate,
        per_page: 300,
      };

      // Backend now handles all permission filtering automatically
      // Admin/Main Secretary: See all appointments
      // Regular Secretary: See only assigned doctor's appointments
      // Doctor: See only own appointments
      const toYMD = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      const [appointmentsData, selectedDateData] = await Promise.all([
        AppointmentService.getAppointments(filters),
        AppointmentService.getAppointments({
          date: toYMD(currentDate),
        }),
      ]);

      // Client-side filtering only for UI purposes (when specific doctors are unselected)
      // This respects the backend permissions while allowing UI filtering
      let filteredAppointments = appointmentsData.data || [];
      let filteredTodayAppointments = selectedDateData.data || [];

      // Only filter client-side if user has deselected some doctors in the UI
      if (
        selectedDoctors.length > 0 &&
        selectedDoctors.length < doctors.length
      ) {
        filteredAppointments = filteredAppointments.filter((apt) =>
          selectedDoctors.includes(apt.doctor_id)
        );
        filteredTodayAppointments = filteredTodayAppointments.filter((apt) =>
          selectedDoctors.includes(apt.doctor_id)
        );
      }

      setAppointments(filteredAppointments);
      setTodaysAppointments(filteredTodayAppointments);
    } catch (err: any) {
      console.error("Error loading appointments:", err);
      setError(err.message || "Failed to load appointments");
    } finally {
      setIsLoading(false);
    }
  };

  const getDateRange = () => {
    const date = new Date(currentDate);

    if (view === "day") {
      const startDate = date.toISOString().split("T")[0];
      const endDate = startDate;
      return { startDate, endDate };
    } else if (view === "month") {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const startDate = new Date(firstDay);
      startDate.setDate(firstDay.getDate() - firstDay.getDay());

      const endDate = new Date(lastDay);
      endDate.setDate(lastDay.getDate() + (6 - lastDay.getDay()));

      return {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      };
    } else {
      // Week view
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - date.getDay());

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return {
        startDate: startOfWeek.toISOString().split("T")[0],
        endDate: endOfWeek.toISOString().split("T")[0],
      };
    }
  };

  // Add custom pricing save handler
  // Add custom pricing save handler
  const handleSaveCustomPricing = async (
    appointmentId: number,
    services: SelectedService[]
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const updatedAppointment = await AppointmentService.saveCustomPricing(
        appointmentId,
        services
      );

      // Update appointment in state
      setAppointments((prev) =>
        prev.map((apt) => (apt.id === appointmentId ? updatedAppointment : apt))
      );

      setTodaysAppointments((prev) =>
        prev.map((apt) => (apt.id === appointmentId ? updatedAppointment : apt))
      );

      // FIX: Ensure proper number calculation
      const totalAmount = services.reduce((sum, service) => {
        const price = service.customPrice || service.service.base_fee;
        return sum + (Number(price) || 0);
      }, 0);

      showSuccessNotification(
        `Custom pricing saved: $${totalAmount.toFixed(2)} for ${
          services.length
        } service${services.length !== 1 ? "s" : ""}`
      );
    } catch (error: any) {
      console.error(`${getIcon("error")} Error saving custom pricing:`, error);
      setError(error.message || "Failed to save custom pricing");
      showErrorNotification(error.message || "Failed to save custom pricing");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };

  const handleViewChange = (newView: "day" | "week" | "month") => {
    setView(newView);
  };

  const handleDoctorToggle = (doctorId: number) => {
    // UPDATED: Only allow doctor toggling if user has permission to see multiple doctors
    if (user?.roles?.includes("admin") || user?.is_main_secretary) {
      // Admin and main secretary can toggle any doctor
      setSelectedDoctors((prev) =>
        prev.includes(doctorId)
          ? prev.filter((id) => id !== doctorId)
          : [...prev, doctorId]
      );
    } else if (user?.roles?.includes("secretary") && user?.assigned_doctor_id) {
      // Regular secretary can only see their assigned doctor - no toggling allowed
      console.log("Regular secretary cannot toggle doctors");
    } else if (user?.roles?.includes("doctor")) {
      // Doctor can only see their own appointments - no toggling allowed
      console.log("Doctor cannot toggle doctors");
    }
  };

  const handleNewAppointment = (slot?: {
    date: string;
    time: string;
    doctorId?: number;
  }) => {
    setSelectedSlot(slot || null);
    setSelectedAppointment(null);
    setPrefilledPatient(null); // Clear any prefilled patient
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setSelectedSlot(null);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setSelectedSlot(null);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleAppointmentAction = async (
    action: "confirm" | "arrive" | "complete" | "cancel" | "no_show" | "delete",
    appointmentId: number,
    reason?: string,
    services?: SelectedService[]
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      if (action === "delete") {
        await AppointmentService.deleteAppointment(appointmentId);

        // Remove appointment from state
        setAppointments((prev) =>
          prev.filter((apt) => apt.id !== appointmentId)
        );
        setTodaysAppointments((prev) =>
          prev.filter((apt) => apt.id !== appointmentId)
        );

        // Show success message
        showSuccessNotification("Appointment deleted successfully");
        return;
      }

      let updatedAppointment: Appointment;
      let revenueInfo: any = null;

      switch (action) {
        case "confirm":
          updatedAppointment = await AppointmentService.confirmAppointment(
            appointmentId
          );
          showSuccessNotification("Appointment confirmed successfully");
          break;

        case "arrive":
          updatedAppointment = await AppointmentService.arriveAppointment(
            appointmentId
          );
          showSuccessNotification("Patient marked as arrived");
          break;

        case "complete":
          // Check if services were provided and use the correct method
          if (services && services.length > 0) {
            // Complete with services and revenue tracking
            const result =
              await AppointmentService.completeAppointmentWithServices(
                appointmentId,
                services
              );

            // âœ… FIX: Handle the actual API response structure
            if (result && typeof result === "object") {
              // Check if result has the nested structure from Laravel
              if ("appointment" in result && "totalRevenue" in result) {
                // Laravel returned: { appointment: Appointment, revenueTransactions: [], totalRevenue: number }
                updatedAppointment = result.appointment;

                showRevenueSuccessNotification({
                  totalRevenue: result.totalRevenue,
                  serviceCount: services.length,
                  transactions: result.revenueTransactions || [],
                });
              } else {
                // Laravel returned just the appointment
                updatedAppointment = result as Appointment;

                // Fallback: Calculate total from services
                const totalAmount = services.reduce((sum, service) => {
                  const price = service.customPrice || service.service.base_fee;
                  return sum + (Number(price) || 0);
                }, 0);

                showSuccessNotification(
                  `Appointment completed with ${services.length} service${
                    services.length !== 1 ? "s" : ""
                  } - Total: $${totalAmount.toFixed(2)}`
                );
              }
            } else {
              throw new Error("Invalid response from server");
            }
          } else {
            // Complete without services
            updatedAppointment = await AppointmentService.completeAppointment(
              appointmentId
            );
            showSuccessNotification("Appointment completed successfully");
          }
          break;
        case "no_show":
          updatedAppointment = await AppointmentService.noShowAppointment(
            appointmentId
          );
          showSuccessNotification("Appointment marked as no-show");
          break;

        case "cancel":
          updatedAppointment = await AppointmentService.cancelAppointment(
            appointmentId,
            reason
          );
          showSuccessNotification("Appointment cancelled successfully");
          break;

        default:
          throw new Error(`Unknown action: ${action}`);
      }

      // Update appointments in state
      setAppointments((prev) =>
        prev.map((apt) => (apt.id === appointmentId ? updatedAppointment : apt))
      );

      setTodaysAppointments((prev) =>
        prev.map((apt) => (apt.id === appointmentId ? updatedAppointment : apt))
      );

      // Reload appointments to ensure we have the latest data from server
      if (action === "complete" && services && services.length > 0) {
        setTimeout(() => {
          loadAppointments();
        }, 1000); // Small delay to ensure server has processed everything
      }
    } catch (err: any) {
      console.error(`Error ${action} appointment:`, err);
      setError(err.message || `Failed to ${action} appointment`);
      showErrorNotification(err.message || `Failed to ${action} appointment`);
      throw err; // Re-throw the error so the calling component can handle it
    } finally {
      setIsLoading(false);
    }
  };

  const showSuccessNotification = (message: string) => {
    // Create a temporary success notification
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: ${isMobile ? "70px" : "20px"};
      right: 20px;
      left: ${isMobile ? "20px" : "auto"};
      background: #10b981;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      z-index: 1000;
      font-weight: 500;
      font-size: 14px;
      max-width: ${isMobile ? "calc(100vw - 40px)" : "350px"};
    `;
    notification.textContent = `${getIcon("success")} ${message}`;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  };

  const showErrorNotification = (message: string) => {
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: ${isMobile ? "70px" : "20px"};
      right: 20px;
      left: ${isMobile ? "20px" : "auto"};
      background: #ef4444;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      z-index: 1000;
      font-weight: 500;
      font-size: 14px;
      max-width: ${isMobile ? "calc(100vw - 40px)" : "350px"};
    `;
    notification.textContent = `${getIcon("error")} ${message}`;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 4000);
  };

  const showRevenueSuccessNotification = (
    revenueInfo: {
      totalRevenue: number;
      serviceCount: number;
      transactions: any[];
    } | null
  ) => {
    // âœ… Add null check
    if (!revenueInfo) {
      console.warn(
        "No revenue data provided to showRevenueSuccessNotification"
      );
      showSuccessNotification("Appointment completed successfully");
      return;
    }

    const notification = document.createElement("div");
    notification.style.cssText = `
    position: fixed;
    top: ${isMobile ? "70px" : "20px"};
    right: 20px;
    left: ${isMobile ? "20px" : "auto"};
    background: #10b981;
    color: white;
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    z-index: 1000;
    font-weight: 500;
    font-size: 14px;
    max-width: ${isMobile ? "calc(100vw - 40px)" : "350px"};
  `;

    // âœ… Ensure all values are numbers with fallbacks
    const totalRevenue = Number(revenueInfo.totalRevenue) || 0;
    const serviceCount = Number(revenueInfo.serviceCount) || 0;
    const transactionCount = Array.isArray(revenueInfo.transactions)
      ? revenueInfo.transactions.length
      : 0;

    const showRevenueSuccessNotification = (revenueInfo: any) => {
      notification.innerHTML = `
    <div style="display: flex; align-items: center; margin-bottom: 8px;">
      <span style="font-size: 16px; margin-right: 8px;">${getIcon(
        "payment"
      )}</span>
      <strong>Appointment Completed Successfully!</strong>
    </div>
    <div style="font-size: 13px; opacity: 0.9;">
      ${serviceCount} service${serviceCount !== 1 ? "s" : ""} • 
      Total: $${totalRevenue.toFixed(2)} • 
      ${transactionCount} revenue transaction${
        transactionCount !== 1 ? "s" : ""
      } created
    </div>
  `;
    };
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 5000);
  };

  const handleAppointmentDelete = async (
    appointmentId: number
  ): Promise<void> => {
    await handleAppointmentAction("delete", appointmentId);
  };

  const handleAppointmentDrop = async (
    appointmentId: number,
    newDate: string,
    newTime: string
  ) => {
    try {
      setIsLoading(true);

      // Call the reschedule API with the correct method signature
      const updatedAppointment = await AppointmentService.rescheduleAppointment(
        appointmentId,
        {
          appointment_date: newDate,
          appointment_time: newTime,
        }
      );

      // Update appointments in state
      setAppointments((prev) =>
        prev.map((apt) => (apt.id === appointmentId ? updatedAppointment : apt))
      );

      setTodaysAppointments((prev) =>
        prev.map((apt) => (apt.id === appointmentId ? updatedAppointment : apt))
      );

      // Refresh appointments to ensure we have latest data
      await loadAppointments();
    } catch (err: any) {
      setError(err.message || "Failed to reschedule appointment");
      // Refresh appointments to revert any optimistic updates
      await loadAppointments();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppointmentSave = async (
    appointmentData: AppointmentFormData,
    newPatientData?: NewPatientData
  ) => {
    try {
      setIsLoading(true);

      let result: {
        appointment: Appointment;
        patient_created?: {
          id: number;
          name: string;
          phone: string;
          note: string;
        };
      };

      if (modalMode === "create") {
        result = await AppointmentService.createAppointment(
          appointmentData,
          newPatientData
        );
      } else {
        // FIXED: For updates, now also support new patient creation
        result = await AppointmentService.updateAppointment(
          selectedAppointment!.id,
          appointmentData,
          newPatientData // ADDED: Pass newPatientData to updateAppointment
        );
      }

      // Show success message with patient creation info if applicable
      if (result.patient_created) {
        // You can replace this with your preferred notification system
        alert(
          `Appointment ${
            modalMode === "create" ? "created" : "updated"
          } successfully!\n\n` +
            `New patient "${result.patient_created.name}" has been created.\n` +
            `${result.patient_created.note}`
        );
      }

      // Refresh appointments
      await loadAppointments();

      if (
        isMobile &&
        (!selectedDoctors.length ||
          selectedDoctors[0] !== result.appointment.doctor_id)
      ) {
        setSelectedDoctors([result.appointment.doctor_id]);
      }

      setIsModalOpen(false);
      setSelectedAppointment(null);
      setSelectedSlot(null);
      setPrefilledPatient(null);
    } catch (err: any) {
      console.log(`${getIcon("error")} Error saving appointment:`, err);

      // Handle specific error cases for better UX
      if (err.message.includes("already exists")) {
        throw new Error(
          "A patient with similar details already exists. " +
            "Please use the search tab to find the existing patient."
        );
      } else {
        throw new Error(err.message || "Failed to save appointment");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
    setSelectedSlot(null);
    setPrefilledPatient(null); // Clear prefilled patient
  };

  const navigateDate = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);

    if (view === "day") {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7));
    } else if (view === "month") {
      newDate.setMonth(newDate.getMonth() + (direction === "next" ? 1 : -1));
    }

    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const goToDate = (dateString: string) => {
    if (dateString) {
      const selectedDate = new Date(dateString);
      setCurrentDate(selectedDate);
    }
  };

  const mobileHeaderContainerStyle: React.CSSProperties = {
    padding: "12px 16px",
    overflowX: "hidden", // FIXED: Prevent horizontal overflow
    // FIXED: Ensure it doesn't exceed container
    width: "100%",
    maxWidth: "100vw",
  };
  const mobileHeaderScrollableStyle: React.CSSProperties = {
    display: "flex",
    overflowX: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#c1c1c1 #f1f1f1",
    gap: "12px",
    // FIXED: Use auto instead of max-content to prevent overflow
    minWidth: "100%",
    width: "max-content",
    paddingRight: "16px", // Add padding to prevent cut-off
  };

  // FIXED: Mobile doctor cards with better responsive width
  const mobileDoctorCardStyle = (date: Date): React.CSSProperties => {
    const isSmallScreen = window.innerWidth <= 480;
    const cardWidth = isSmallScreen ? 100 : 120;

    return {
      background: isToday(date) ? "#eff6ff" : "#f9fafb",
      borderRadius: "8px",
      padding: isSmallScreen ? "6px 8px" : "8px 12px",
      width: `${cardWidth}px`,
      flexShrink: 0,
      textAlign: "center",
      border: "1px solid #e5e7eb",
      // FIXED: Ensure proper height
      minHeight: isSmallScreen ? "60px" : "70px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    };
  };
  // UPDATED: Add helper function to check if user can toggle doctors
  const canUserToggleDoctors = (): boolean => {
    return user?.roles?.includes("admin") || user?.is_main_secretary || false;
  };

  // UPDATED: Add helper function to get user role display
  const getUserRoleDisplay = (): string => {
    if (user?.roles?.includes("admin")) {
      return "Administrator";
    } else if (user?.roles?.includes("secretary")) {
      if (user?.is_main_secretary) {
        return "Main Secretary";
      } else {
        const assignedDoctor = doctors.find(
          (d) => d.id === user?.assigned_doctor_id
        );
        return assignedDoctor
          ? `Secretary (${assignedDoctor.name})`
          : "Secretary";
      }
    } else if (user?.roles?.includes("doctor")) {
      return "Doctor";
    }
    return "User";
  };

  // Responsive Styles
  const containerStyle: React.CSSProperties = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #E0F7FA 0%, #80DEEA 100%)",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    maxWidth: "100vw",
    width: "100%",
  };

  const headerStyle: React.CSSProperties = {
    background: "#ffffff",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    padding: isMobile ? "16px 20px" : "20px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "sticky",
    top: 0,
    zIndex: 100,
    borderBottom: "1px solid #e5e7eb",
    width: "100%",
    maxWidth: "100vw",
    overflowX: "hidden",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: isMobile ? "18px" : "24px",
    fontWeight: "600",
    color: "#1f2937",
    margin: 0,
  };

  const headerControlsStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: isMobile ? "6px" : "16px",
    // flex: isMobile ? "1" : "initial",
    justifyContent: isMobile ? "space-between" : "flex-end",
    flexWrap: isMobile ? "wrap" : "nowrap",
    maxWidth: isMobile ? "70%" : "none",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "outline" | "nav" = "secondary"
  ): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      padding: isMobile ? "8px 12px" : "8px 16px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      maxHeight: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px solid transparent",
    };

    switch (variant) {
      case "primary":
        return {
          ...baseStyle,
          background: "#3b82f6",
          color: "#ffffff",
          border: "1px solid #3b82f6",
        };
      case "outline":
        return {
          ...baseStyle,
          background: "#ffffff",
          color: "#374151",
          border: "1px solid #d1d5db",
        };
      case "nav":
        return {
          ...baseStyle,
          background: "#ffffff",
          color: "#374151",
          border: "1px solid #d1d5db",
          padding: isMobile ? "8px" : "8px 12px",
          minWidth: isMobile ? "40px" : "auto",
        };
      default:
        return {
          ...baseStyle,
          background: "#f3f4f6",
          color: "#374151",
        };
    }
  };

  const contentStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    flex: 1,
    height: isMobile ? "auto" : "calc(100vh - 80px)",
    background: "#ffffff",
    overflowX: "hidden",
    maxWidth: "100vw",
    width: "100%",
  };
  const calculateCalendarWidth = (): number => {
    const baseWidth = 80; // Time column width
    const doctorColumnWidth = isMobile ? 150 : 200;
    const doctorsCount = Math.max(filteredDoctors.length, 1);

    if (view === "week") {
      // Week view: 7 days
      return baseWidth + 7 * doctorColumnWidth * doctorsCount;
    } else {
      // Day view: selected doctors
      return baseWidth + doctorsCount * doctorColumnWidth;
    }
  };
  const mainContentStyle: React.CSSProperties = {
    flex: isMobile ? "1" : sidebarCollapsed ? "1" : "0 0 70%",
    transition: "flex 0.3s ease",
    borderRight: isMobile ? "none" : "1px solid #e5e7eb",
    order: isMobile ? 2 : 1,
    // ADD: Enable horizontal scrolling
    overflowX: "auto",
    overflowY: "hidden",
    minWidth: 0, // Important for flex items to shrink properly
  };
  const calendarContainerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    overflowX: "auto",
    overflowY: "auto",
    // Ensure minimum width for calendar grid
    minWidth: view === "week" ? "800px" : "400px", // Adjust based on your needs
  };
  const enhancedStyles = `
  /* Calendar horizontal scrolling */
  .calendar-container {
    width: 100%;
    overflow-x: auto;
    overflow-y: auto;
  }
  
  .calendar-grid {
    min-width: ${view === "week" ? "800px" : "400px"};
    width: 100%;
  }
  
  /* Week view specific styling */
  .week-view .calendar-grid {
    min-width: 900px; /* Ensure enough space for 7 days */
    display: grid;
    grid-template-columns: 80px repeat(7, 1fr);
  }
  
  /* Day view specific styling */
  .day-view .calendar-grid {
    min-width: 600px; /* Ensure enough space for single day with doctors */
    display: grid;
    grid-template-columns: 80px repeat(auto-fit, minmax(200px, 1fr));
  }
  
  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    .calendar-container {
      -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    }
    
    .week-view .calendar-grid {
      min-width: 1200px; /* Larger on mobile to prevent cramping */
      grid-template-columns: 60px repeat(7, minmax(150px, 1fr));
    }
    
    .day-view .calendar-grid {
      min-width: 800px;
      grid-template-columns: 60px repeat(auto-fit, minmax(180px, 1fr));
    }
    
    /* Mobile doctor columns */
    .doctor-column {
      min-width: 150px;
      max-width: 200px;
    }
  }
  
  /* Smaller mobile devices */
  @media (max-width: 480px) {
    .week-view .calendar-grid {
      min-width: 1000px;
      grid-template-columns: 50px repeat(7, minmax(130px, 1fr));
    }
    
    .day-view .calendar-grid {
      min-width: 700px;
      grid-template-columns: 50px repeat(auto-fit, minmax(160px, 1fr));
    }
    
    .doctor-column {
      min-width: 130px;
      max-width: 180px;
    }
  }
  
  /* Custom scrollbar for horizontal scroll */
  .calendar-container::-webkit-scrollbar {
    height: 8px;
    width: 6px;
  }
  
  .calendar-container::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  .calendar-container::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  
  .calendar-container::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  /* Ensure appointment slots maintain proper width */
  .appointment-slot {
    min-width: 100%;
    width: 100%;
    box-sizing: border-box;
  }
  
  /* Time column styling */
  .time-column {
    position: sticky;
    left: 0;
    background: white;
    border-right: 1px solid #e5e7eb;
    z-index: 10;
    min-width: 60px;
  }
  
  @media (max-width: 768px) {
    .time-column {
      min-width: 50px;
    }
  }
    @keyframes highlightPulse {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
      }
      50% {
        box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
      }
    }
    
    .highlight-appointment {
      animation: highlightPulse 1s ease-in-out 2;
      border: 2px solid #3b82f6 !important;
    }
`;
  const sidebarStyle: React.CSSProperties = {
    flex: isMobile ? "none" : sidebarCollapsed ? "0 0 60px" : "0 0 30%",
    transition: "flex 0.3s ease",
    background: "#f8fafc",
    borderLeft: isMobile ? "none" : "1px solid #e5e7eb",
    borderTop: isMobile ? "1px solid #e5e7eb" : "none",
    height: isMobile ? "auto" : "100%",
    order: isMobile ? 1 : 2,
    position: isMobile ? "relative" : "static",
  };

  // Mobile bottom sheet for appointments
  const mobileBottomSheetStyle: React.CSSProperties = {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#ffffff",
    borderTopLeftRadius: "16px",
    borderTopRightRadius: "16px",
    boxShadow: "0 -10px 25px rgba(0, 0, 0, 0.15)",
    zIndex: 200,
    transform: mobileBottomSheetOpen
      ? "translateY(0)"
      : "translateY(calc(100% - 60px))",
    transition: "transform 0.3s ease",
    maxHeight: "80vh",
    overflow: "hidden",
  };

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Appointments</h1>
        </div>
        <div
          style={{
            padding: isMobile ? "20px" : "40px",
            textAlign: "center",
            color: "#ef4444",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <h3 style={{ fontSize: isMobile ? "18px" : "24px" }}>
            Error loading appointments
          </h3>
          <p
            style={{
              fontSize: isMobile ? "14px" : "16px",
              marginBottom: "20px",
            }}
          >
            {error}
          </p>
          <button
            onClick={() => {
              setError(null);
              loadAppointments();
            }}
            style={buttonStyle("primary")}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle} className="nav-controls">
      {/* Header */}
      <div style={headerStyle} className="nav-controls">
        {/* Left side - Month/Year + Role Display */}
        <div>
          <h1 style={titleStyle}>
            {view === "month"
              ? currentDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })
              : currentDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
          </h1>
          {/* ADDED: Show user role and permissions */}
          <div
            style={{
              fontSize: "12px",
              color: "#6b7280",
              marginTop: "4px",
            }}
          >
            {getUserRoleDisplay()} . {selectedDoctors.length}/{doctors.length}{" "}
            doctors
          </div>
        </div>

        {/* Right side - Controls */}
        <div style={headerControlsStyle} className="nav-controls">
          {/* Mobile: Date display on same line */}
          {/* {isMobile && (
            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1f2937",
                flex: 1,
                textAlign: "left",
              }}
            >
              {currentDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </div>
          )} */}

          {/* Desktop: View Toggle */}
          {!isMobile && (
            <div
              style={{
                display: "flex",
                background: "#f3f4f6",
                borderRadius: "8px",
                padding: "2px",
                marginRight: "8px",
              }}
            >
              <button
                onClick={() => handleViewChange("day")}
                style={{
                  ...buttonStyle("secondary"),
                  background: view === "day" ? "#ffffff" : "transparent",
                  color: view === "day" ? "#1f2937" : "#6b7280",
                  border: view === "day" ? "1px solid #d1d5db" : "none",
                  margin: 0,
                  padding: "6px 12px",
                  fontSize: "13px",
                  boxShadow:
                    view === "day" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                }}
              >
                Day
              </button>
              <button
                onClick={() => handleViewChange("week")}
                style={{
                  ...buttonStyle("secondary"),
                  background: view === "week" ? "#ffffff" : "transparent",
                  color: view === "week" ? "#1f2937" : "#6b7280",
                  border: view === "week" ? "1px solid #d1d5db" : "none",
                  margin: 0,
                  padding: "6px 12px",
                  fontSize: "13px",
                  boxShadow:
                    view === "week" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                }}
              >
                Week
              </button>
              <button
                onClick={() => handleViewChange("month")}
                style={{
                  ...buttonStyle("secondary"),
                  background: view === "month" ? "#ffffff" : "transparent",
                  color: view === "month" ? "#1f2937" : "#6b7280",
                  border: view === "month" ? "1px solid #d1d5db" : "none",
                  margin: 0,
                  padding: "6px 12px",
                  fontSize: "13px",
                  boxShadow:
                    view === "month" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                }}
              >
                Month
              </button>
            </div>
          )}

          {/* Navigation Controls Group */}
          <div
            className="nav-controls"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {/* Navigation arrows */}
            <button
              onClick={() => navigateDate("prev")}
              style={buttonStyle("nav")}
              title="Previous"
            >
              {getIcon("back")}
            </button>

            {/* Desktop: Today button */}
            {!isMobile && (
              <button onClick={goToToday} style={buttonStyle("outline")}>
                Today
              </button>
            )}

            <button
              onClick={() => navigateDate("next")}
              style={buttonStyle("nav")}
              title="Next"
            >
              {getIcon("forward")}
            </button>

            {/* Mobile: 3-dot menu */}
            {isMobile && (
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  style={buttonStyle("nav")}
                  title="More options"
                >
                  {getIcon("menu")}
                </button>

                {/* Mobile dropdown menu */}
                {mobileMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 199,
                        backgroundColor: "transparent", // Add transparent background
                      }}
                      onClick={() => setMobileMenuOpen(false)}
                    />

                    {/* Menu */}
                    <div
                      className="mobile-dropdown"
                      style={{
                        position: "fixed", // Changed from absolute to fixed
                        top: "70px", // Position below the header
                        right: "20px", // Align with right edge
                        left: "20px", // Add left margin for mobile
                        background: "#ffffff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "12px",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                        zIndex: 200,
                        maxWidth: "280px", // Limit max width
                        marginLeft: "auto", // Center align with max width
                        padding: "8px",
                        // Ensure it doesn't get cut off
                        maxHeight: "calc(100vh - 100px)",
                        overflowY: "auto",
                      }}
                    >
                      <button
                        className="nav-controls"
                        onClick={() => {
                          handleNewAppointment();
                          setMobileMenuOpen(false);
                        }}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: "500",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          background: "transparent",
                          color: "#374151",
                          textAlign: "left",
                          transition: "background 0.2s ease",
                          marginBottom: "4px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f3f4f6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span>{getIcon("add")}</span>
                        Book Appointment
                      </button>

                      <button
                        className="nav-controls"
                        onClick={() => {
                          goToToday();
                          setMobileMenuOpen(false);
                        }}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: "500",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          background: "transparent",
                          color: "#374151",
                          textAlign: "left",
                          transition: "background 0.2s ease",
                          marginBottom: "4px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f3f4f6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span>{getIcon("calendar")}</span>
                        Go to today
                      </button>

                      <hr
                        style={{
                          margin: "8px 0",
                          border: "none",
                          borderTop: "1px solid #f3f4f6",
                        }}
                      />

                      <button
                        onClick={() => {
                          handleViewChange("day");
                          setMobileMenuOpen(false);
                        }}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: "500",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          background:
                            view === "day" ? "#f0f9ff" : "transparent",
                          color: view === "day" ? "#0369a1" : "#374151",
                          textAlign: "left",
                          transition: "background 0.2s ease",
                          marginBottom: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                        onMouseEnter={(e) => {
                          if (view !== "day") {
                            e.currentTarget.style.background = "#f3f4f6";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (view !== "day") {
                            e.currentTarget.style.background = "transparent";
                          }
                        }}
                      >
                        Day view
                        {view === "day" && (
                          <span style={{ color: "#0369a1" }}>
                            {getIcon("success")}
                          </span>
                        )}
                      </button>

                      <button
                        onClick={() => {
                          handleViewChange("week");
                          setMobileMenuOpen(false);
                        }}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: "500",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          background:
                            view === "week" ? "#f0f9ff" : "transparent",
                          color: view === "week" ? "#0369a1" : "#374151",
                          textAlign: "left",
                          transition: "background 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                        onMouseEnter={(e) => {
                          if (view !== "week") {
                            e.currentTarget.style.background = "#f3f4f6";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (view !== "week") {
                            e.currentTarget.style.background = "transparent";
                          }
                        }}
                      >
                        Week view
                        {view === "week" && (
                          <span style={{ color: "#0369a1" }}>
                            {getIcon("success")}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          handleViewChange("month");
                          setMobileMenuOpen(false);
                        }}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: "500",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          background:
                            view === "month" ? "#f0f9ff" : "transparent",
                          color: view === "month" ? "#0369a1" : "#374151",
                          textAlign: "left",
                          transition: "background 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                        onMouseEnter={(e) => {
                          if (view !== "month") {
                            e.currentTarget.style.background = "#f3f4f6";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (view !== "month") {
                            e.currentTarget.style.background = "transparent";
                          }
                        }}
                      >
                        Month view
                        {view === "month" && (
                          <span style={{ color: "#0369a1" }}>
                            {getIcon("success")}
                          </span>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Desktop: New Appointment button */}
          {!isMobile && (
            <button
              onClick={() => handleNewAppointment()}
              style={{
                ...buttonStyle("primary"),
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginLeft: "8px",
              }}
            >
              <span>{getIcon("add")}</span>
              New Appointment
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={contentStyle}>
        {/* Calendar Grid */}
        <div style={mainContentStyle}>
          <div className="calendar-container" style={calendarContainerStyle}>
            <AppointmentCalendar
              appointments={appointments}
              currentDate={currentDate}
              view={view}
              doctors={doctors}
              patients={patients} // ADD: Required for book appointment functionality
              selectedDoctors={selectedDoctors}
              onDateChange={handleDateChange}
              onAppointmentClick={handleAppointmentClick}
              onSlotClick={handleNewAppointment}
              onAppointmentDrop={handleAppointmentDrop}
              onAppointmentAction={handleAppointmentAction}
              onSaveCustomPricing={handleSaveCustomPricing}
              onAppointmentSave={handleAppointmentSave} // ADD: For book appointment save
              // onAppointmentEdit={handleAppointmentEdit} // ADD: For appointment editing
              onAppointmentDelete={handleAppointmentDelete} // ADD: For appointment deletion
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Sidebar - Desktop or Mobile */}
        {isMobile ? (
          // FIXED: Mobile header - show doctors as horizontal scrollable cards with better overflow handling
          <div className="nav-controls" style={mobileHeaderContainerStyle}>
            {/* <div style={mobileHeaderContainerStyle}> */}
            {/* Doctor filter section - only show if user can toggle doctors */}
            {canUserToggleDoctors() && doctors.length > 1 && (
              <div
                style={{
                  marginBottom: "12px",
                  padding: "8px 0",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <div
                  className="nav-controls"
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>
                    Filter Doctors ({selectedDoctors.length}/{doctors.length})
                  </span>
                  <span style={{ fontSize: "11px", color: "#6b7280" }}>
                    {getUserRoleDisplay()}
                  </span>
                </div>

                {/* FIXED: Horizontal scrollable doctor chips */}
                <div
                  className="nav-controls"
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    scrollbarWidth: "thin",
                    flexDirection: "row",
                    alignItems: "stretch !important",
                    scrollbarColor: "#c1c1c1 #f1f1f1",
                    gap: "8px",
                    paddingBottom: "4px",
                    // CRITICAL: Prevent container overflow
                    width: "100%",
                    maxWidth: "100%",
                  }}
                >
                  {doctors
                    .sort((a, b) => a.id - b.id) // Sort by ID for consistency
                    .map((doctor) => (
                      <label
                        htmlFor={`doctor_selection_${doctor.id}`}
                        className="nav-controls"
                        key={doctor.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          background: selectedDoctors.includes(doctor.id)
                            ? "#eff6ff"
                            : "#f9fafb",
                          border: selectedDoctors.includes(doctor.id)
                            ? "2px solid #3b82f6"
                            : "1px solid #e5e7eb",
                          borderRadius: "20px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: "500",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          // FIXED: Prevent chip shrinking and set proper width
                          flexShrink: 0,
                          minWidth: "fit-content",
                          whiteSpace: "nowrap",
                          color: selectedDoctors.includes(doctor.id)
                            ? "#1e40af"
                            : "#374151",
                        }}
                      >
                        <input
                          type={isMobile ? "radio" : "checkbox"}
                          id={`doctor_selection_${doctor.id}`}
                          name="doctor_selection"
                          checked={selectedDoctors.includes(doctor.id)}
                          onChange={() => {
                            if (isMobile) {
                              // Mobile: Select only this doctor (radio behavior)
                              setSelectedDoctors([doctor.id]);
                            } else {
                              // Desktop: Keep existing toggle behavior
                              handleDoctorToggle(doctor.id);
                            }
                          }}
                          style={{
                            marginRight: "6px",
                            minWidth: "14px",
                            minHeight: "14px",
                          }}
                        />

                        <span>Dr. {doctor.first_name}</span>
                      </label>
                    ))}
                </div>
              </div>
            )}

            {/* Current appointments view */}
            {/* <div style={mobileHeaderScrollableStyle}>
              {dateColumns.map((date) =>
                filteredDoctors.map((doctor) => (
                  <div
                    key={`${formatDate(date)}-${doctor.id}`}
                    style={mobileDoctorCardStyle(date)}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        marginBottom: "2px",
                        color: "#1f2937",
                        
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Dr. {doctor.first_name}
                    </div>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#6b7280",
                      }}
                    >
                      {date.toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                    {doctor.specialty && (
                      <div
                        style={{
                          fontSize: "9px",
                          color: "#9ca3af",
                          marginTop: "2px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {doctor.specialty}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div> */}
          </div>
        ) : (
          // Desktop: Normal sidebar
          <div style={sidebarStyle}>
            <AppointmentSidebar
              appointments={todaysAppointments}
              doctors={doctors}
              selectedDoctors={selectedDoctors}
              currentDate={currentDate}
              onDateChange={handleDateChange}
              onDoctorToggle={handleDoctorToggle}
              onAppointmentClick={handleAppointmentClick}
              onAppointmentAction={handleAppointmentAction}
              onSaveCustomPricing={handleSaveCustomPricing}
              onNewAppointment={() => handleNewAppointment()}
              collapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
              isLoading={isLoading}
              // ADDED: Pass permission checks to sidebar
              canToggleDoctors={canUserToggleDoctors()}
              userRole={getUserRoleDisplay()}
            />
          </div>
        )}
      </div>

      {/* Mobile Bottom Sheet */}
      {isMobile && (
        <div style={mobileBottomSheetStyle}>
          {/* Handle bar */}
          <div
            style={{
              width: "40px",
              height: "4px",
              background: "#d1d5db",
              borderRadius: "2px",
              margin: "8px auto",
              cursor: "pointer",
            }}
            onClick={() => setMobileBottomSheetOpen(!mobileBottomSheetOpen)}
          />

          <AppointmentSidebar
            appointments={todaysAppointments}
            doctors={doctors}
            selectedDoctors={selectedDoctors}
            currentDate={currentDate}
            onDateChange={handleDateChange}
            onDoctorToggle={handleDoctorToggle}
            onAppointmentClick={handleAppointmentClick}
            onAppointmentAction={handleAppointmentAction}
            onSaveCustomPricing={handleSaveCustomPricing}
            onNewAppointment={() => handleNewAppointment()}
            collapsed={false}
            onToggleCollapse={() => {}}
            isLoading={isLoading}
            // ADDED: Pass permission checks to sidebar
            canToggleDoctors={canUserToggleDoctors()}
            userRole={getUserRoleDisplay()}
          />
        </div>
      )}

      {/* Appointment Modal */}
      {isModalOpen && (
        <AppointmentModal
          isOpen={isModalOpen}
          mode={modalMode}
          appointment={selectedAppointment}
          selectedSlot={selectedSlot}
          prefilledPatient={prefilledPatient}
          doctors={doctors}
          patients={patients}
          onSave={handleAppointmentSave}
          onEdit={() => setModalMode("edit")}
          onDelete={handleAppointmentDelete}
          onClose={handleModalClose}
          onSaveCustomPricing={handleSaveCustomPricing}
          isLoading={isLoading}
          onOpenPatientDetails={(patientId: number) => {
            // Optional: Handle opening patient details from parent
            console.log("Opening patient details for:", patientId);
          }}
        />
      )}

      {/* Global Styles */}
      <style>
        {`
    ${enhancedStyles}
    
    @media (max-width: 768px) {
      .appointment-grid {
        grid-template-columns: 1fr !important;
      }
      
      .appointment-slot {
        min-height: 60px;
      }
      
      .appointment-card {
        font-size: 12px;
        padding: 8px;
      }
    }
    
    @media (max-width: 480px) {
      .toolbar {
        gap: 8px;
      }
      
      .date-navigation {
        width: 100%;
        justify-content: space-between;
      }
    }
    
    /* Touch targets for mobile */
    @media (max-width: 768px) {
      button, input, select {
        min-height: 44px;
        min-width: 44px;
      }
    }
    
    /* Smooth transitions */
    * {
      box-sizing: border-box;
    }
    
    .mobile-bottom-sheet {
      touch-action: pan-y;
    }
    
    /* Custom scrollbar for better mobile experience */
    ::-webkit-scrollbar {
      width: 6px;
    }
    
    ::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    
    ::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
    }
    
    ::-webkit-scrollbar-thumb:hover {
      background: #a1a1a1;
    }
    
    /* Mobile menu animations */
    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .mobile-dropdown {
      animation: slideIn 0.2s ease-out;
    }
  `}
      </style>
    </div>
  );
};

export default Appointments;
